// Single-run detail for the per-run "transactions" overlay (spec §3.3 / §5, D7).
//
// Fetches one full job row via `admin_pipeline_jobs` { mode: 'detail' } and
// renders the heavy diagnostic JSON:
//   - event jobs → `imports[]` (per-event valid / duplicate / corrected + messages)
//   - place jobs → `modules_output[]` (per-module ✓/✗ for the 14-module chain)
//                  + place_created
//
// SECURITY: all job/log strings are rendered as React children → auto-escaped.
// NEVER dangerouslySetInnerHTML on any of these values.
import { useEffect, useState } from "react";
import { invokeAdmin, errMsg } from "../../lib/adminApi";
import type { JobDetail as JobDetailT } from "../../lib/types";
import { Badge, Banner } from "../ui";

// Best-effort one-line summary of an opaque result blob (valid/duplicate/etc.).
function summarize(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    // common shapes: { valid: bool }, { isDuplicate: bool }, { corrected: bool }, { ok }
    for (const k of ["valid", "isValid", "isDuplicate", "duplicate", "corrected", "ok", "status"]) {
      if (k in o) return `${k}: ${String(o[k])}`;
    }
    try {
      return JSON.stringify(v).slice(0, 120);
    } catch {
      return "[object]";
    }
  }
  return String(v);
}

export default function JobDetail({
  table,
  id,
  onBack,
}: {
  table: "event" | "place";
  id: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<JobDetailT | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-request loading/error reset when the request key changes; results are only written after the await, guarded by the `cancelled` flag
    setLoading(true);
    setError(null);
    invokeAdmin<{ job: JobDetailT }>("admin_pipeline_jobs", { mode: "detail", table, id })
      .then((r) => {
        if (!cancelled) setDetail(r.job);
      })
      .catch((e) => {
        if (!cancelled) setError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [table, id]);

  return (
    <div className="mt-2">
      <button
        onClick={onBack}
        className="mb-2 text-xs text-zinc-400 hover:text-zinc-200"
      >
        ← back to recent runs
      </button>

      {loading && <p className="text-xs text-zinc-500">Loading run…</p>}
      {error && <Banner className="mt-1">{error}</Banner>}

      {detail && (
        <div className="space-y-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Badge ok={detail.statusSuccess !== false}>
              {detail.statusSuccess === false ? "failed" : "ok"}
            </Badge>
            {detail.statusType && (
              <span className="text-zinc-400">{detail.statusType}</span>
            )}
            <span className="font-mono text-[10px] text-zinc-500">{detail.id}</span>
          </div>
          {detail.statusMessage && (
            <p className="text-zinc-400">{detail.statusMessage}</p>
          )}

          {/* event-job → per-event imports[] breakdown */}
          {table === "event" && (
            <div className="rounded border border-zinc-800">
              <div className="border-b border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] uppercase tracking-wide text-zinc-500">
                Imports ({detail.imports?.length ?? 0})
              </div>
              {detail.imports && detail.imports.length > 0 ? (
                <ul className="divide-y divide-zinc-800">
                  {detail.imports.map((imp, i) => (
                    <li key={i} className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-2 text-[11px] text-zinc-300">
                        <span>valid: {summarize(imp.validationResult)}</span>
                        <span>dup: {summarize(imp.duplicateResult)}</span>
                        <span>corrected: {summarize(imp.correctionResult)}</span>
                      </div>
                      {imp.messages && imp.messages.length > 0 && (
                        <ul className="mt-0.5 list-disc pl-4 text-[10px] text-zinc-500">
                          {imp.messages.map((msg, j) => (
                            <li key={j}>{msg}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-2 py-1.5 text-[11px] text-zinc-500">No per-event detail.</p>
              )}
            </div>
          )}

          {/* place-job → modules_output[] pass/fail + place_created */}
          {table === "place" && (
            <>
              <div className="text-[11px] text-zinc-400">
                place created:{" "}
                <Badge ok={!!detail.placeCreated}>
                  {detail.placeCreated ? "yes" : "no"}
                </Badge>
              </div>
              <div className="rounded border border-zinc-800">
                <div className="border-b border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] uppercase tracking-wide text-zinc-500">
                  Modules ({detail.modulesOutput?.length ?? 0})
                </div>
                {detail.modulesOutput && detail.modulesOutput.length > 0 ? (
                  <ul className="divide-y divide-zinc-800">
                    {detail.modulesOutput.map((mod, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between px-2 py-1 text-[11px]"
                      >
                        <span className="font-mono text-zinc-300">{mod.name}</span>
                        <span className={mod.success ? "text-emerald-400" : "text-red-400"}>
                          {mod.success ? "✓" : "✗"}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-2 py-1.5 text-[11px] text-zinc-500">No module output.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
